import { SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { SegmentTariffParameterDirectionType, SegmentTariffParameterValueType, TariffCategoryType } from '@/constants';
import CompanyTicketClassInput from '@/components/FormItems/CompanyTicketClassInput';
import { CompanyTicketClassDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageTicketTariff } from '@/hooks/useHasPermissions';

const columns: IColumnType<CompanyTicketClassDto>[] = [
  {
    title: 'ticketTariffsPage_Segment_Category',
    dataIndex: 'tariffParameterCategory',
    ellipsis: true,
    render: v => TariffCategoryType.__getValue(v),
  },
  {
    title: 'ticketTariffsPage_Segment_Code',
    dataIndex: 'tariffParameterCode',
    ellipsis: true,
  },
  {
    title: 'ticketTariffsPage_Segment_Name',
    dataIndex: 'tariffParameterName',
    ellipsis: true,
  },
  {
    title: 'baggageTariffsPage_TicketClass',
    dataIndex: 'companyTicketClassName',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'ticketTariffsPage_Segment_ValueType',
    dataIndex: 'parameterValueType',
    ellipsis: true,
    render: v => SegmentTariffParameterValueType.__getValue(v),
  },
  {
    title: 'ticketTariffsPage_Segment_Value',
    dataIndex: 'parameterValue',
    ellipsis: true,
  },
  { title: 'ticketTariffsPage_Segment_ReturnValue', dataIndex: 'parameterReturnValue', ellipsis: true },

  {
    title: 'ticketTariffsPage_Segment_Direction',
    dataIndex: 'parameterDirection',
    ellipsis: true,
    render: v => SegmentTariffParameterDirectionType.__getValue(v),
  },
];

const filters: Array<IFilterItem> = [
  {
    type: 'LIST',
    name: 'category',
    label: 'ticketTariffsPage_Segment_Category',
    mdSize: 8,
    lgSize: 8,
    xlSize: 8,
    xxlSize: 8,
    listData: TariffCategoryType.__getSelectOptions(),
  },
  {
    type: 'TEXT',
    name: 'searchText',
    label: 'ticketTariffsPage_Segment_SearchText',
    mdSize: 8,
    lgSize: 8,
    xlSize: 8,
    xxlSize: 8,
  },
];
type Props = {
  onClose: () => void;
  tariffId: string;
};

const SegmentTariffParametersModal = ({ onClose, tariffId }: Props) => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const canUpdate = useCanManageTicketTariff();

  const updateEntity = (values, applyForAllSegments: boolean) => {
    setLoading(true);
    apiSdk.BasicTariffService.saveSegmentTariffParameter({
      body: {
        ...values,

        ticketTariffId: tariffId,
        applyForAllSegments,
      },
    })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={t('ticketTariffsPage_Segment_Tariff')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={() => {
        onClose();
      }}
    >
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        defaultFilter={{ ticketTariffId: tariffId }}
        url="/fleets/basic-tariffs/get-segment-tariff-parameters"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={canUpdate}
        onEdit={record => {
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={false}
        filters={filters}
        searchBtnAlignLeft
      ></ZTable>
      <Modal
        visible={formVisible}
        maskClosable={false}
        width={ModalWidth.small}
        className="nta-custom-form"
        title={t('ticketTariffsPage_Segment_UpdateTariffParameter')}
        forceRender={true}
        okText={t('save')}
        cancelText={t('cancel')}
        okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
        onCancel={() => {
          form.resetFields();
          setFormVisible(false);
        }}
        footer={
          <Row>
            <Col md={6} style={{ textAlign: 'left' }}>
              <Button
                onClick={() => {
                  form.resetFields();
                  setFormVisible(false);
                }}
              >
                {t('cancel')}
              </Button>
            </Col>
            <Col md={18}>
              <Button
                type="primary"
                onClick={() => {
                  form
                    .validateFields()
                    .then(values => {
                      updateEntity(values, false);
                    })
                    .catch(info => {});
                }}
                className="save-btn"
                loading={loading}
                icon={<SaveOutlined />}
              >
                {t('save')}
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => {
                  form
                    .validateFields()
                    .then(values => {
                      Modal.confirm({
                        title: t('ticketTariffsPage_Segment_ApplyForAllSegmentsConfirm'),
                        onOk: () => {
                          updateEntity(values, true);
                        },
                      });
                    })
                    .catch(info => {});
                }}
                loading={loading}
                icon={<SaveOutlined />}
              >
                {t('ticketTariffsPage_Segment_ApplyForAllSegments')}
              </Button>
            </Col>
          </Row>
        }
      >
        <Spin spinning={loading}>
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>

            <Form.Item name="baseTariffParameterId" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name="segmentTariffParameterId" className="hidden">
              <Input type="hidden" />
            </Form.Item>

            <Row gutter={ROW_GUTTER}>
              <Col md={24}>
                <Form.Item
                  label={t('ticketTariffsPage_Segment_Value')}
                  name="parameterValue"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item
                  label={t('ticketTariffsPage_Segment_ReturnValue')}
                  name="parameterReturnValue"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item
                  label={t('ticketTariffsPage_Segment_Direction')}
                  name="parameterDirection"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Select options={SegmentTariffParameterDirectionType.__getSelectOptions()} />
                </Form.Item>
                <Form.Item
                  label={t('ticketTariffsPage_Segment_ValueType')}
                  name="parameterValueType"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Select options={SegmentTariffParameterValueType.__getSelectOptions()} />
                </Form.Item>
                <Form.Item
                  label={t('baggageTariffsPage_TicketClass')}
                  name="baseTicketClassId"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <CompanyTicketClassInput disabled className="input-value-color" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </Modal>
  );
};
export default SegmentTariffParametersModal;
